import { matchPath } from 'react-router-dom';
import {
  FREEMIUM_CHECKOUT_CONVERSION_EXPERIMENT_ID,
  FREEMIUM_EXPERIMENT_IDS,
  FREEMIUM_MINI_QUIZ_EXPERIMENT_ID,
  FREEMIUM_REENGAGEMENT_EXPERIMENT_ID,
  isFreemiumReengagementTreatment,
  isFreemiumTreatment,
  toCanonicalId,
  toCanonicalVariant,
} from '@hungryroot/experiments';
import {
  log,
  logExceptionOnce,
  logUnknownErrorResponse,
} from '@hungryroot/o11y';
import {
  getCustomerExperiments,
  getCustomerSelf,
  postApiV2CustomersCustomerIdResetSurvey,
  StatusCode,
  type GetCustomerExperimentsResponse,
} from '@hungryroot/rest';
import {
  DISCOVER_FEED_ROUTE,
  MY_HUNGRYROOT_ROUTE,
  ROUTE_MINI_QUIZ_FOOD_FORMATS,
  ROUTE_MINI_QUIZ_INDEX,
  ROUTE_QUIZ_HELLO,
  START_ORDER_ROUTE,
} from '@hungryroot/routes';
import {
  DEFAULT_QUIZ_STORAGE_NAMESPACE,
  getStartedFunnel,
  MINI_QUIZ_STORAGE_NAMESPACE,
} from '../defs/localStorage';
import { resolveExplicitSelectionsIntroRoute } from '../explicit-selections/resolveExplicitSelectionsIntroRoute';
import { type LoginQuizPersistOutcome } from '../login-persist/persistLocalQuizOnLogin';
import { isExp255MiniQuizViewport } from '../navigation/exp255MiniQuizViewport';
import { resolveReengagementFunnelNamespace } from '../navigation/resolveReengagementFunnelNamespace';

/** The slice of the customer this flow needs; `SignupResponse` and `Customer` both satisfy it. */
export interface PostLoginCustomer {
  id: number;
  did_checkout: boolean;
}

interface Params {
  /**
   * The customer who just logged in, when the login response carried one
   * (SSO). Omit to have the flow fetch `/customers/self/`.
   */
  customer?: PostLoginCustomer;
  /**
   * The result of attempting to persist this browser's completed quiz data.
   * A reset is safe only when there was no local candidate to persist.
   */
  quizPersistOutcome?: LoginQuizPersistOutcome;
  /**
   * Freemium is a mobile-only experience (same breakpoint as every freemium
   * route gate). Defaults to the live viewport; injectable for tests.
   */
  isMobile?: boolean;
}

/**
 * Upper bound on how long a login redirect waits for this resolution. Past
 * it the caller's default destination wins.
 */
const RESOLVE_DEADLINE_MS = 10_000;

/**
 * Whether a `/login/` `?next=` is one the freemium post-login route may
 * replace. Only two values qualify: the default `/myhungryroot` (no `next`
 * at all — a guest is bounced off it to the feed anyway) and any
 * `/mini-quiz/*` path, which is what the mini-quiz signup screen's "Log in"
 * link sends. A real deep link — a gift-card redeem, a checked-out route
 * the guard asked for — is honoured as before.
 */
export function isFreemiumReplaceableLoginNext(next: string): boolean {
  let pathname: string;
  try {
    pathname = new URL(next, 'https://hungryroot.com').pathname;
  } catch {
    return false;
  }

  return (
    matchPath(pathname, { path: MY_HUNGRYROOT_ROUTE, strict: false }) !==
      null ||
    matchPath(pathname, { path: ROUTE_MINI_QUIZ_INDEX, strict: false }) !== null
  );
}

async function fetchCustomer(): Promise<PostLoginCustomer | null> {
  const resp = await getCustomerSelf();
  if (resp.status !== StatusCode.SuccessOK) {
    logUnknownErrorResponse('Failed to load customer after login', {
      key: 'resolveFreemiumPostLoginRoute.customerSelf',
      status: resp.status,
    });
    return null;
  }
  return resp.data;
}

type ExperimentAssignmentState =
  | 'control'
  | 'excluded'
  | 'treatment'
  | 'unenrolled';

interface CanonicalExperimentAssignment {
  experimentId: number;
  state: ExperimentAssignmentState;
  variant: number;
}

function resolveAssignmentState(variant: number): ExperimentAssignmentState {
  if (variant < 0) {
    return 'excluded';
  }
  if (variant === 0) {
    return 'control';
  }
  return 'treatment';
}

function readCanonicalAssignment(
  rows: GetCustomerExperimentsResponse['results'],
  canonicalId: number
): CanonicalExperimentAssignment {
  const row = rows
    .filter((candidate) => toCanonicalId(candidate.experiment) === canonicalId)
    .sort((a, b) => b.experiment - a.experiment)[0];
  if (row === undefined) {
    return { experimentId: canonicalId, state: 'unenrolled', variant: 0 };
  }

  const variant = toCanonicalVariant(row.variant);
  return {
    experimentId: canonicalId,
    state: resolveAssignmentState(variant),
    variant,
  };
}

/**
 * Which freemium arm the signed-in account is in:
 *
 *   - `reengagement` — Exp 260 v1, with or without an Exp 255 treatment
 *   - `mini-quiz`    — an Exp 255 treatment (v1/v2) alone
 *   - `none`         — control or unenrolled in both
 *
 * Read from the backend — the cookie is ignored once authenticated — with
 * the same `isFreemiumTreatment` / `isFreemiumReengagementTreatment` the
 * route gates and the Exp 260 UI use. `null` when the read failed.
 */
type FreemiumArm = 'mini-quiz' | 'reengagement' | 'none';

interface FreemiumAssignment {
  arm: FreemiumArm;
  experiments: CanonicalExperimentAssignment[];
  isExp255Treatment: boolean;
}

async function fetchFreemiumAssignment(): Promise<FreemiumAssignment | null> {
  const resp = await getCustomerExperiments({});
  if (resp.status !== StatusCode.SuccessOK) {
    logUnknownErrorResponse('Failed to load experiments after login', {
      key: 'resolveFreemiumPostLoginRoute.experiments',
      status: resp.status,
    });
    return null;
  }

  const experiments = FREEMIUM_EXPERIMENT_IDS.map((experimentId) =>
    readCanonicalAssignment(resp.data.results, experimentId)
  );
  const variant255 =
    experiments.find(
      ({ experimentId }) => experimentId === FREEMIUM_MINI_QUIZ_EXPERIMENT_ID
    )?.variant ?? 0;
  const variant260 =
    experiments.find(
      ({ experimentId }) => experimentId === FREEMIUM_REENGAGEMENT_EXPERIMENT_ID
    )?.variant ?? 0;
  const variant266 =
    experiments.find(
      ({ experimentId }) =>
        experimentId === FREEMIUM_CHECKOUT_CONVERSION_EXPERIMENT_ID
    )?.variant ?? 0;
  const variants = { variant255, variant260, variant266 };

  if (!isFreemiumTreatment(variants)) {
    return { arm: 'none', experiments, isExp255Treatment: false };
  }

  return {
    arm: isFreemiumReengagementTreatment(variants)
      ? 'reengagement'
      : 'mini-quiz',
    experiments,
    isExp255Treatment: variant255 > 0,
  };
}

function logPostLoginDecision({
  assignment,
  customerId,
  destination,
  didResetSurvey,
  funnel,
  quizPersistOutcome,
  reason,
}: {
  assignment: FreemiumAssignment;
  customerId: number;
  destination: string;
  didResetSurvey: boolean;
  funnel: 'mini-quiz' | 'quiz' | null;
  quizPersistOutcome: LoginQuizPersistOutcome | undefined;
  reason:
    | 'completed'
    | 'explicit-selections'
    | 'not-treatment'
    | 'survey-reset';
}) {
  assignment.experiments.forEach(({ experimentId, state, variant }) => {
    log('Resolved freemium post-login route', {
      key: 'resolveFreemiumPostLoginRoute.decision',
      customerId,
      experimentId,
      variant,
      assignment: state,
      arm: assignment.arm,
      funnel,
      destination,
      didResetSurvey,
      quizPersistOutcome: quizPersistOutcome ?? null,
      reason,
    });
  });
}

async function resetSurveyForNoCandidate({
  customerId,
  quizPersistOutcome,
}: {
  customerId: number;
  quizPersistOutcome: LoginQuizPersistOutcome | undefined;
}): Promise<boolean> {
  if (quizPersistOutcome !== 'no-candidate') {
    return false;
  }

  try {
    const resp = await postApiV2CustomersCustomerIdResetSurvey({ customerId });
    if (resp.status !== StatusCode.SuccessOK) {
      logUnknownErrorResponse('Failed to reset survey after login', {
        key: 'resolveFreemiumPostLoginRoute.resetSurvey',
        status: resp.status,
      });
      return false;
    }

    log('Resolved post-login survey reset', {
      key: 'resolveFreemiumPostLoginRoute.resetSurvey.completed',
      customerId,
      reset: resp.data.reset,
    });
    return resp.data.reset;
  } catch (error) {
    logExceptionOnce(error, {
      key: 'resolveFreemiumPostLoginRoute.resetSurvey',
    });
    return false;
  }
}

async function run({
  customer,
  isMobile,
  quizPersistOutcome,
}: Params): Promise<string | null> {
  if (isMobile === false) {
    return null;
  }

  const resolvedCustomer = customer ?? (await fetchCustomer());
  if (resolvedCustomer === null || resolvedCustomer.did_checkout) {
    return null;
  }

  const assignment = await fetchFreemiumAssignment();
  if (assignment === null) {
    return null;
  }
  if (assignment.arm === 'none') {
    logPostLoginDecision({
      assignment,
      customerId: resolvedCustomer.id,
      destination: 'default',
      didResetSurvey: false,
      funnel: null,
      quizPersistOutcome,
      reason: 'not-treatment',
    });
    return null;
  }

  // Exp 255-only guests keep the existing mini-quiz intro. Funnel affinity
  // is applied only to the Exp 260/266 re-engagement path.
  const funnel =
    assignment.arm === 'reengagement'
      ? resolveReengagementFunnelNamespace({
          isMobile: true,
          startedFunnel: getStartedFunnel(),
          isExp255Treatment: assignment.isExp255Treatment,
        })
      : MINI_QUIZ_STORAGE_NAMESPACE;

  const didResetSurvey = await resetSurveyForNoCandidate({
    customerId: resolvedCustomer.id,
    quizPersistOutcome,
  });
  if (didResetSurvey) {
    const destination =
      funnel === DEFAULT_QUIZ_STORAGE_NAMESPACE
        ? ROUTE_QUIZ_HELLO
        : ROUTE_MINI_QUIZ_FOOD_FORMATS;
    logPostLoginDecision({
      assignment,
      customerId: resolvedCustomer.id,
      destination,
      didResetSurvey,
      funnel,
      quizPersistOutcome,
      reason: 'survey-reset',
    });
    return destination;
  }

  const introRoute = await resolveExplicitSelectionsIntroRoute({
    customerId: resolvedCustomer.id,
    funnel,
    source: 'login',
  });
  if (introRoute !== null) {
    logPostLoginDecision({
      assignment,
      customerId: resolvedCustomer.id,
      destination: introRoute,
      didResetSurvey,
      funnel,
      quizPersistOutcome,
      reason: 'explicit-selections',
    });
    return introRoute;
  }

  const destination =
    assignment.arm === 'reengagement' ? START_ORDER_ROUTE : DISCOVER_FEED_ROUTE;
  logPostLoginDecision({
    assignment,
    customerId: resolvedCustomer.id,
    destination,
    didResetSurvey,
    funnel,
    quizPersistOutcome,
    reason: 'completed',
  });
  return destination;
}

/**
 * Exp 255 / Exp 260: where a freemium-treatment guest goes after logging in.
 *
 *   - no local persist candidate + backend survey reset → designated funnel
 *     start
 *   - explicit selections done, Exp 260 treatment → `/start-order/`
 *   - explicit selections done, Exp 255 treatment alone → `/discover/feed/`
 *   - not yet, Exp 255 alone → `/mini-quiz/food-preferences-intro/`
 *   - not yet, Exp 260/266 → the active funnel's intro; with no active local
 *     funnel, an Exp 255 overlap implies mini-quiz and otherwise full-quiz
 *
 * A guest in both treatments follows Exp 260, like the Exp 260 UI
 * (`isFreemiumReengagementTreatment`).
 *
 * `null` for everyone else — checked-out customers, control / unenrolled
 * accounts, or desktop — so the caller keeps its own destination. A failed
 * reset check falls back to explicit-selection routing. Never throws and
 * never takes longer than
 * {@link RESOLVE_DEADLINE_MS}: login must complete whatever happens here.
 *
 * Call it *after* `persistLocalQuizOnLogin` and pass that outcome. The backend
 * reset check runs only for `no-candidate`; running it after successful or
 * uncertain persistence could erase quiz answers.
 */
export async function resolveFreemiumPostLoginRoute({
  customer,
  isMobile = isExp255MiniQuizViewport(),
  quizPersistOutcome,
}: Params = {}): Promise<string | null> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timeout = setTimeout(() => {
      logExceptionOnce('Freemium post-login resolution exceeded its deadline', {
        key: 'resolveFreemiumPostLoginRoute.timedOut',
      });
      resolve(null);
    }, RESOLVE_DEADLINE_MS);
  });

  try {
    return await Promise.race([
      run({ customer, isMobile, quizPersistOutcome }),
      deadline,
    ]);
  } catch (error) {
    logExceptionOnce(error, {
      key: 'resolveFreemiumPostLoginRoute.unexpected',
    });
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
